import React from 'react';
import { useTranslation } from 'react-i18next';

import { INITIAL_STATE } from '../constants';
import { AnimationStage, AnimationState, MarketScanConfig, PackageConfig, StageDefinition } from '../types';
import { wait } from '../utils';

import { useBatchReveal } from './useBatchReveal';
import { useProgressSimulation } from './useProgressSimulation';
import { useStatusRotation } from './useStatusRotation';
import { useVisibleCompetitors } from './useVisibleCompetitors';

type ProgressStage = 'model-prep' | 'analyzing' | 'preparing-packs';

const PROGRESS_STAGES: ProgressStage[] = ['model-prep', 'analyzing', 'preparing-packs'];

function getStageDefinition(config: MarketScanConfig, stage: AnimationStage): StageDefinition | null {
  switch (stage) {
    case 'model-prep':
      return config.stages.modelPrep;
    case 'analyzing':
      return config.stages.analyzing;
    case 'preparing-packs':
      return config.stages.preparingPacks;
    default:
      return null;
  }
}

function getStatusTexts(config: MarketScanConfig, stage: AnimationStage): string[] {
  switch (stage) {
    case 'model-prep':
      return config.statusTexts.modelPrep;
    case 'analyzing':
      return config.statusTexts.analyzing;
    case 'preparing-packs':
      return config.statusTexts.preparingPacks;
    default:
      return [];
  }
}

let hasFinishedOnce = false;

export function resetMarketScanAnimation() {
  hasFinishedOnce = false;
}

export function setMarketScanAnimationFinished() {
  hasFinishedOnce = true;
}

export function isMarketScanAnimationFinished(): boolean {
  return hasFinishedOnce;
}

function buildFinalState(config: MarketScanConfig, quickReveal = false): AnimationState {
  const targetPackage = config.packages.find((p) => p.isSelected) ?? config.packages[0];
  const labelledIds = config.packages.filter((p) => p.label).map((p) => p.id);

  return {
    stage: 'display-packs',
    progress: 100,
    currentStatusText: '',
    statusTextRevealing: false,
    revealedBatches: config.competitors.map((_, i) => i),
    visibleCompetitors: config.competitors.flatMap((b) => b.competitors),
    analyzedCompetitorIds: config.competitors.flatMap((b) => b.competitors.map((c) => c.id)),
    competitorsVisible: false,
    revealedPackageIds: config.packages.map((p) => p.id),
    shiningPackageId: null,
    expandedPackageId: targetPackage?.id ?? null,
    tagsVisiblePackageIds: labelledIds,
    selectedPackageId: targetPackage?.id ?? null,
    competitorsMini: true,
    benchmarkVisible: true,
    featurePackVisible: true,
    footerVisible: true,
    isOpen: true,
    quickReveal,
    featureModalIdx: null,
    drawerOpenPackageId: null,
    configureScreenOpen: false,
    configureFeatureIdx: 0,
    configureFeatureSelections: {},
    configurePackSaved: false,
  };
}

export function useAnimationEngine(config: MarketScanConfig) {
  const { t } = useTranslation();
  const [state, setState] = React.useState<AnimationState>(() =>
    hasFinishedOnce ? buildFinalState(config) : INITIAL_STATE,
  );
  const abortRef = React.useRef<AbortController | null>(null);
  const mountedRef = React.useRef(true);
  const timersRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);

  const isProgressStage = PROGRESS_STAGES.includes(state.stage as ProgressStage);
  const stageDef = getStageDefinition(config, state.stage);
  const statusTexts = getStatusTexts(config, state.stage);

  const progress = useProgressSimulation(stageDef, isProgressStage);
  const { text: statusText, revealing: statusRevealing } = useStatusRotation(
    statusTexts,
    config.statusTexts.rotationIntervalMs,
    isProgressStage,
  );
  // Batch reveal activates when competitorsVisible is set (fixed delay from start)
  const revealedBatches = useBatchReveal(config.competitors, state.competitorsVisible);
  // Per-competitor staggered reveal — exposed on state for declarative consumers
  const { visible: visibleCompetitors, analyzedIds: analyzedCompetitorIds } = useVisibleCompetitors(
    config.competitors,
    state.competitorsVisible,
  );

  const derivedState = React.useMemo<AnimationState>(
    () => ({
      ...state,
      progress: isProgressStage ? progress : state.stage === 'display-packs' ? 100 : state.progress,
      // Status texts are treated as i18n keys (English copy is the fallback).
      currentStatusText: isProgressStage ? (statusText ? t(statusText) : '') : state.currentStatusText,
      statusTextRevealing: isProgressStage ? statusRevealing : false,
      revealedBatches: state.competitorsVisible ? revealedBatches : state.revealedBatches,
      visibleCompetitors: state.competitorsVisible ? visibleCompetitors : state.visibleCompetitors,
      analyzedCompetitorIds: state.competitorsVisible ? analyzedCompetitorIds : state.analyzedCompetitorIds,
    }),
    [state, isProgressStage, progress, statusText, statusRevealing, revealedBatches, visibleCompetitors, analyzedCompetitorIds, t],
  );

  const start = React.useCallback(() => {
    abortRef.current?.abort();
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    if (hasFinishedOnce) {
      // Re-build final state if packages weren't loaded when state was initialized (e.g. after page refresh)
      setState((prev) => (prev.revealedPackageIds.length > 0 ? prev : buildFinalState(config, true)));
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    const run = async () => {
      // Intro
      setState({ ...INITIAL_STATE, stage: 'intro', isOpen: true });

      // Schedule competitors visibility at 3.5s from start (matches original competitorsDelay = 3500)
      const competitorTimer = setTimeout(() => {
        if (!signal.aborted) {
          setState((s) => ({ ...s, competitorsVisible: true }));
        }
      }, 3500);
      timersRef.current.push(competitorTimer);

      await wait(config.timing.introDurationMs, signal);
      if (signal.aborted) return;

      // UI Init
      setState((s) => ({ ...s, stage: 'ui-init' }));
      await wait(config.timing.uiInitDurationMs, signal);
      if (signal.aborted) return;

      // Model Prep
      setState((s) => ({ ...s, stage: 'model-prep' }));
      await wait(config.stages.modelPrep.durationMs, signal);
      if (signal.aborted) return;

      // Analyzing
      setState((s) => ({ ...s, stage: 'analyzing' }));
      await wait(config.stages.analyzing.durationMs, signal);
      if (signal.aborted) return;

      // Preparing Packs
      setState((s) => ({ ...s, stage: 'preparing-packs' }));
      await wait(config.stages.preparingPacks.durationMs, signal);
      if (signal.aborted) return;

      // Display Packs — move competitors mini and show benchmark immediately when reveal starts
      setState((s) => ({
        ...s,
        stage: 'display-packs',
        progress: 100,
        competitorsMini: true,
        benchmarkVisible: true,
      }));

      // Staggered package reveal sequence (matches prototype)
      await wait(config.timing.packageRevealInitialDelayMs, signal);
      if (signal.aborted) return;

      // Staggered package reveal sequence: Left (0), Right (2), Center (1) if 3 packages
      const pkgCount = config.packages.length;
      let revealSequence: PackageConfig[] = [];
      if (pkgCount === 3) {
        revealSequence = [config.packages[0], config.packages[2], config.packages[1]];
      } else {
        revealSequence = [...config.packages];
      }

      for (const pkg of revealSequence) {
        if (!pkg) continue;
        setState((s) => ({ ...s, revealedPackageIds: [...s.revealedPackageIds, pkg.id] }));
        await wait(config.timing.packageRevealStaggerMs, signal);
        if (signal.aborted) return;
      }

      const targetPackage = config.packages.find((p) => p.isSelected) ?? config.packages[0];

      if (!targetPackage) return;

      await wait(config.timing.packageExpandDelayMs, signal);
      if (signal.aborted) return;

      setState((s) => ({ ...s, expandedPackageId: targetPackage.id, shiningPackageId: targetPackage.id }));
      await wait(config.timing.packageShiningDurationMs, signal);
      if (signal.aborted) return;

      setState((s) => ({ ...s, shiningPackageId: null, selectedPackageId: targetPackage.id }));
      await wait(config.timing.packageTagsDelayMs, signal);
      if (signal.aborted) return;

      const labelledIds = config.packages.filter((p) => p.label).map((p) => p.id);
      setState((s) => ({ ...s, tagsVisiblePackageIds: labelledIds }));

      await wait(config.timing.featurePackDelayMs, signal);
      if (signal.aborted) return;
      setState((s) => ({ ...s, featurePackVisible: true }));

      await wait(config.timing.footerDelayMs, signal);
      if (signal.aborted) return;
      setState((s) => ({ ...s, footerVisible: true }));

      hasFinishedOnce = true;
    };

    run();
  }, [config]);

  const close = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setState({ ...INITIAL_STATE, stage: 'closed' });
  }, []);

  const selectPackage = React.useCallback((packageId: string) => {
    setState((s) => ({ ...s, selectedPackageId: packageId, expandedPackageId: packageId }));
  }, []);

  const openFeatureModal = React.useCallback((idx: number) => {
    setState((s) => ({ ...s, featureModalIdx: idx }));
  }, []);

  const closeFeatureModal = React.useCallback(() => {
    setState((s) => ({ ...s, featureModalIdx: null }));
  }, []);

  const openPackageDrawer = React.useCallback((packageId: string) => {
    setState((s) => ({ ...s, drawerOpenPackageId: packageId }));
  }, []);

  const closePackageDrawer = React.useCallback(() => {
    setState((s) => ({ ...s, drawerOpenPackageId: null }));
  }, []);

  const openConfigureScreen = React.useCallback(() => {
    setState((s) => ({ ...s, configureScreenOpen: true }));
  }, []);

  const closeConfigureScreen = React.useCallback(() => {
    setState((s) => ({ ...s, configureScreenOpen: false }));
  }, []);

  const selectConfigureFeature = React.useCallback((idx: number) => {
    setState((s) => ({ ...s, configureFeatureIdx: idx }));
  }, []);

  /**
   * Seed a default tier for a feature if the user hasn't picked one yet.
   * Called from the configure-screen carousel cell so every visited feature
   * carries a "chosen" state (drives the check + the badge label).
   */
  const seedConfigureTier = React.useCallback(
    (itemId: string | number, tierId: string | number) => {
      setState((s) => {
        const key = String(itemId);
        if (s.configureFeatureSelections[key] != null) return s;
        return {
          ...s,
          configureFeatureSelections: { ...s.configureFeatureSelections, [key]: tierId },
        };
      });
    },
    [],
  );

  const selectConfigureTier = React.useCallback(
    (itemId: string | number, tierId: string | number) => {
      setState((s) => ({
        ...s,
        configureFeatureSelections: { ...s.configureFeatureSelections, [String(itemId)]: tierId },
        // Any deliberate tier pick promotes the modal to "saved" — independent
        // of whether session.items happens to match campaign defaults.
        configurePackSaved: true,
      }));
    },
    [],
  );

  const clearConfigureSelections = React.useCallback(() => {
    setState((s) => ({ ...s, configureFeatureSelections: {}, configurePackSaved: false }));
  }, []);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      timersRef.current.forEach(clearTimeout);
    };
  }, []);

  return {
    state: derivedState,
    start,
    close,
    selectPackage,
    openFeatureModal,
    closeFeatureModal,
    openPackageDrawer,
    closePackageDrawer,
    openConfigureScreen,
    closeConfigureScreen,
    selectConfigureFeature,
    selectConfigureTier,
    seedConfigureTier,
    clearConfigureSelections,
  };
}
